import { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { MANAGEMENT_ROLE_ID } from '@treams/treams-analytics';

import type { GroupSelection } from '../../goals/group-selector/new/GroupSelector.types';
import type { GroupSelectionData } from '../../goals/group-selector/new/useGroupSelection';
import {
	type DepartmentToken,
	TALENT_REVIEW_DEPARTMENTS_KEY,
	TALENT_REVIEW_GRID_KEY,
	TALENT_REVIEW_ROLE_KEY,
	applyTalentReviewSearch,
	matchDepartmentTokens,
	parseDepartmentsParam,
	parseRoleParam,
	parseTalentReviewSearch,
	resolveTemplateID,
	serializeDepartmentsParam,
} from './talentReviewAnalyticsSearch';

const ALLOWED_ROLES = Object.values(MANAGEMENT_ROLE_ID);

type TemplateOption = { value: string };

type UrlDepartments = { tokens: DepartmentToken[] | undefined };

const filtersKey = (
	grid: string | undefined,
	role: string | undefined,
	departments: string | undefined,
) => `${grid ?? ''}|${role ?? ''}|${departments ?? ''}`;

export function useTalentReviewAnalyticsSearch(params: {
	templateOptions: TemplateOption[];
	groupSelection: GroupSelectionData;
}) {
	const { templateOptions, groupSelection } = params;
	const history = useHistory();
	const location = useLocation();

	const urlDepartmentsRef = useRef<UrlDepartments | null>(null);
	if (urlDepartmentsRef.current === null) {
		urlDepartmentsRef.current = {
			tokens: parseDepartmentsParam(
				new URLSearchParams(location.search).get(TALENT_REVIEW_DEPARTMENTS_KEY),
			),
		};
	}

	const [templateID, setTemplateID] = useState<string | undefined>(
		() => new URLSearchParams(location.search).get(TALENT_REVIEW_GRID_KEY) ?? undefined,
	);
	const [roleID, setRoleID] = useState<string | undefined>(() =>
		parseRoleParam(new URLSearchParams(location.search).get(TALENT_REVIEW_ROLE_KEY), ALLOWED_ROLES),
	);
	const [departmentsReady, setDepartmentsReady] = useState(
		() => urlDepartmentsRef.current?.tokens === undefined,
	);

	const templateIDs = useMemo(
		() => templateOptions.map((option) => option.value),
		[templateOptions],
	);
	const resolvedTemplateID = resolveTemplateID(templateID, templateIDs);
	const hasSyncedSearchRef = useRef(false);
	const lastFiltersKeyRef = useRef('');

	useEffect(() => {
		if (resolvedTemplateID !== templateID) {
			setTemplateID(resolvedTemplateID);
		}
	}, [resolvedTemplateID, templateID]);

	useEffect(() => {
		if (groupSelection.loading || departmentsReady) {
			return;
		}

		applyDepartmentTokens(urlDepartmentsRef.current?.tokens, groupSelection);
		setDepartmentsReady(true);
		// Apply the URL once groups exist. updateExplicitGroupSelection is not stable.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [groupSelection.loading, departmentsReady]);

	const explicitDepartments = explicitDepartmentsParam(groupSelection);

	useEffect(() => {
		if (history.action === 'POP' && hasSyncedSearchRef.current) {
			restorePoppedSearch({
				search: location.search,
				templateIDs,
				groupSelection,
				setTemplateID,
				setRoleID,
				urlDepartmentsRef,
				setDepartmentsReady,
				lastFiltersKeyRef,
			});
			return;
		}

		if (!departmentsReady || groupSelection.loading || !resolvedTemplateID) {
			return;
		}

		writeTalentReviewSearch({
			history,
			// DateIntervalNavigation may have pushed interval/cursor/end in this
			// same commit. history.location already has that push; the location
			// from this render does not, and writing it back would drop the period.
			location: history.location,
			resolvedTemplateID,
			roleID,
			explicitDepartments,
			hasSyncedSearchRef,
			lastFiltersKeyRef,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		departmentsReady,
		groupSelection.loading,
		resolvedTemplateID,
		roleID,
		explicitDepartments,
		location.search,
		templateIDs,
	]);

	const handleDepartmentChange = (selection: GroupSelection[]) => {
		groupSelection.updateExplicitGroupSelection(selection);
	};

	return {
		templateID: templateIDs.length === 0 ? undefined : resolvedTemplateID,
		roleID,
		setTemplateID,
		setRoleID,
		onDepartmentChange: handleDepartmentChange,
	};
}

function explicitDepartmentsParam(groupSelection: GroupSelectionData) {
	if (!groupSelection.hasExplicitGroupSelection) {
		return undefined;
	}

	return serializeDepartmentsParam(
		groupSelection.groupSelection.map(({ group, recursive }) => ({
			id: group.id,
			recursive,
		})),
	);
}

function matchedDepartmentsParam(
	tokens: DepartmentToken[] | undefined,
	groupSelection: GroupSelectionData,
) {
	if (tokens === undefined || tokens.length === 0) {
		return undefined;
	}

	return serializeDepartmentsParam(
		matchDepartmentTokens(tokens, groupSelection.selectableGroups).map(({ group, recursive }) => ({
			id: group.id,
			recursive,
		})),
	);
}

function restorePoppedSearch({
	search,
	templateIDs,
	groupSelection,
	setTemplateID,
	setRoleID,
	urlDepartmentsRef,
	setDepartmentsReady,
	lastFiltersKeyRef,
}: {
	search: string;
	templateIDs: string[];
	groupSelection: GroupSelectionData;
	setTemplateID: (templateID: string | undefined) => void;
	setRoleID: (roleID: string | undefined) => void;
	urlDepartmentsRef: { current: UrlDepartments | null };
	setDepartmentsReady: (ready: boolean) => void;
	lastFiltersKeyRef: { current: string };
}) {
	const parsed = parseTalentReviewSearch(search, ALLOWED_ROLES);
	const nextGrid = resolveTemplateID(parsed.grid, templateIDs);

	setTemplateID(nextGrid);
	setRoleID(parsed.role);
	urlDepartmentsRef.current = { tokens: parsed.departments };

	if (!groupSelection.loading) {
		applyDepartmentTokens(parsed.departments, groupSelection);
		setDepartmentsReady(true);
	} else {
		setDepartmentsReady(parsed.departments === undefined);
	}

	lastFiltersKeyRef.current = filtersKey(
		nextGrid,
		parsed.role,
		matchedDepartmentsParam(parsed.departments, groupSelection),
	);
}

function writeTalentReviewSearch({
	history,
	location,
	resolvedTemplateID,
	roleID,
	explicitDepartments,
	hasSyncedSearchRef,
	lastFiltersKeyRef,
}: {
	history: ReturnType<typeof useHistory>;
	location: ReturnType<typeof useLocation>;
	resolvedTemplateID: string;
	roleID: string | undefined;
	explicitDepartments: string | undefined;
	hasSyncedSearchRef: { current: boolean };
	lastFiltersKeyRef: { current: string };
}) {
	const nextSearch = applyTalentReviewSearch(location.search, {
		grid: resolvedTemplateID,
		role: roleID,
		departments: explicitDepartments,
	});
	const currentSearch = location.search.replace(/^\?/, '');
	const nextFiltersKey = filtersKey(resolvedTemplateID, roleID, explicitDepartments);

	if (currentSearch === nextSearch) {
		lastFiltersKeyRef.current = nextFiltersKey;
		hasSyncedSearchRef.current = true;
		return;
	}

	const nextLocation = {
		...location,
		search: nextSearch ? `?${nextSearch}` : '',
	};

	if (hasSyncedSearchRef.current && lastFiltersKeyRef.current !== nextFiltersKey) {
		history.push(nextLocation);
	} else {
		history.replace(nextLocation);
	}

	lastFiltersKeyRef.current = nextFiltersKey;
	hasSyncedSearchRef.current = true;
}

function applyDepartmentTokens(
	tokens: DepartmentToken[] | undefined,
	groupSelection: GroupSelectionData,
) {
	if (tokens === undefined || tokens.length === 0) {
		if (groupSelection.hasExplicitGroupSelection) {
			groupSelection.updateExplicitGroupSelection([]);
		}
		return;
	}

	const matched = matchDepartmentTokens(tokens, groupSelection.selectableGroups);

	if (
		matchedDepartmentsParam(tokens, groupSelection) === explicitDepartmentsParam(groupSelection)
	) {
		return;
	}

	groupSelection.updateExplicitGroupSelection(matched);
}
